import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { MapIcon, ExclamationCircleIcon, CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Button, Card, Input } from 'components/gsys-ui'
import { useState, useEffect } from 'react'
import ModalContainer from 'components/ModalContainer'
import {
    getAddressOverride,
    saveAddressOverride,
    removeAddressOverride
} from 'util/deliveryAddressOverrides'

const DeliveryAddressModal = NiceModal.create(({ address, docNo, onOverrideChange }) => {
    const modal = useModal()
    const [coordinates, setCoordinates] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)
    const [successMessage, setSuccessMessage] = useState(null)

    // State for original address and edited version
    const [originalAddress, setOriginalAddress] = useState(address)
    const [editedAddress, setEditedAddress] = useState(address)

    // Extract postcode using regex for UK postcodes
    const postcodeRegex = /[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}/gi
    const postcodeMatch = editedAddress.match(postcodeRegex)
    const hasPostcode = !!postcodeMatch

    // State for manual postcode entry
    const [postcode, setPostcode] = useState(hasPostcode ? postcodeMatch[0] : '')

    // Check if there's a saved override
    useEffect(() => {
        if (docNo) {
            const savedOverride = getAddressOverride(docNo)
            if (savedOverride) {
                if (savedOverride.address) {
                    setEditedAddress(savedOverride.address)
                }
                if (savedOverride.lat && savedOverride.lon) {
                    setCoordinates({
                        lat: savedOverride.lat,
                        lon: savedOverride.lon
                    })
                    setSuccessMessage("Using saved delivery address coordinates")
                }
            }
        }
    }, [docNo])

    const lookupPostcode = async () => {
        if (!postcode.trim()) {
            setError("Please enter a valid postcode")
            return
        }

        setIsLoading(true)
        setError(null)
        setSuccessMessage(null)

        try {
            // Call the postcodes.io API
            const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`)
            const data = await res.json()

            if (data.status === 200) {
                setCoordinates({
                    lat: data.result.latitude,
                    lon: data.result.longitude
                })
                setSuccessMessage("Coordinates found successfully")
            } else {
                setError("Couldn't find coordinates for this postcode")
            }
        } catch (err) {
            setError("Error looking up postcode")
            console.error('Error looking up postcode:', err)
        } finally {
            setIsLoading(false)
        }
    }

    const handleSaveOverride = () => {
        if (!coordinates || !docNo) return

        const saved = saveAddressOverride(docNo, {
            address: editedAddress,
            lat: coordinates.lat,
            lon: coordinates.lon,
            postcode: postcode
        })

        if (saved) {
            setSuccessMessage("Delivery address saved successfully")
            // Call the callback if provided to trigger a re-render
            if (typeof onOverrideChange === 'function') {
                onOverrideChange(docNo)
            }
        } else {
            setError("Failed to save delivery address")
        }
    }

    const handleClearOverride = () => {
        if (!docNo) return

        const removed = removeAddressOverride(docNo)
        if (removed) {
            // Reset to original values
            setEditedAddress(originalAddress)
            setCoordinates(null)
            setSuccessMessage("Delivery address override cleared")
            // Call the callback if provided to trigger a re-render
            if (typeof onOverrideChange === 'function') {
                onOverrideChange(docNo)
            }
        } else {
            setError("Failed to clear delivery address override")
        }
    }

    return (
        <ModalContainer>
            <Card className="!w-[500px]">
                <Card.Title Icon={MapIcon}>
                    Delivery Address
                </Card.Title>
                <Card.Body>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address</label>
                        <textarea
                            className="w-full rounded-md border border-gray-300 p-2 h-24"
                            value={editedAddress}
                            onChange={(e) => setEditedAddress(e.target.value)}
                        />
                    </div>

                    <div className="mb-4">
                        {hasPostcode ? (
                            <div className="text-green-600 mb-2 flex items-center">
                                <CheckCircleIcon className="w-5 h-5 mr-1" />
                                <span>Postcode detected: {postcodeMatch[0]}</span>
                            </div>
                        ) : (
                            <div className="flex items-center text-amber-500 mb-2">
                                <ExclamationCircleIcon className="w-5 h-5 mr-1" />
                                <span>No valid postcode detected</span>
                            </div>
                        )}

                        <div className="flex items-center space-x-2">
                            <Input
                                value={postcode}
                                onChange={(e) => setPostcode(e.target.value)}
                                placeholder="Enter postcode"
                                className="w-48"
                            />
                            <Button
                                disabled={isLoading}
                                onClick={lookupPostcode}
                            >
                                {isLoading ? "Loading..." : "Lookup"}
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <div className="text-red-500 mb-4 p-2 bg-red-50 rounded-md">
                            <ExclamationCircleIcon className="w-5 h-5 inline mr-1" />
                            {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className="text-green-600 mb-4 p-2 bg-green-50 rounded-md">
                            <CheckCircleIcon className="w-5 h-5 inline mr-1" />
                            {successMessage}
                        </div>
                    )}

                    {coordinates && (
                        <div className="border p-3 rounded-md bg-gray-50 mb-4">
                            <h4 className="text-sm font-medium mb-2">Location Coordinates</h4>
                            <div className="mb-1">Latitude: {coordinates.lat}</div>
                            <div className="mb-3">Longitude: {coordinates.lon}</div>
                            <div className="flex space-x-2">
                                <Button
                                    className="flex-1"
                                    onClick={() => window.open(`https://maps.google.com/?q=${coordinates.lat},${coordinates.lon}`, '_blank')}
                                >
                                    View on Map
                                </Button>
                                <Button
                                    className="flex-1"
                                    variant="primary"
                                    onClick={handleSaveOverride}
                                >
                                    Save Override
                                </Button>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-between mt-4">
                        <Button
                            variant="danger"
                            onClick={handleClearOverride}
                            Icon={TrashIcon}
                            disabled={!getAddressOverride(docNo)}
                        >
                            Clear Override
                        </Button>
                        <Button variant="white" onClick={modal.remove}>Close</Button>
                    </div>
                </Card.Body>
            </Card>
        </ModalContainer>
    )
})

export default DeliveryAddressModal